// FlujoRedes — el flujo de Instagram B2C explicado paso a paso (26-jul-2026).
// Nace del feedback de Nico: "aprobé un plan creyendo que aprobaba piezas, no vi
// ni texto ni imagen, y después no encontré nada". Este archivo centraliza:
//   - PASOS_FLUJO + <FlujoStepper/>: los 5 pasos del pipeline, con el actual resaltado.
//   - cargarEstadoFlujo(): lee planes_semanales + calendario_marketing + posts_ig
//     y devuelve UNA lectura en criollo de dónde está parada la semana.
//   - <CasoActualCard/>: la tarjeta "dónde estamos / qué falta / quién lo hace".
//   - <BotonAgente/> + correrEdge(): disparar cualquier agente a mano, sin esperar cron.
// Lo usan ComunicacionHub (Panel), ComunicacionPlan y ComunicacionPiezas.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL

// ─────────────────────────────────────────────────────────────────────────────
// Los 5 pasos. quien: 'vos' = te toca a vos (gold) · 'agente' = corre solo (teal)
// ─────────────────────────────────────────────────────────────────────────────
export const PASOS_FLUJO = [
  {
    n: 1, emoji: '🧠', quien: 'agente', corto: 'Estratega propone',
    detalle: 'El Estratega decide QUÉ comunicar esta semana: temas, fechas y horarios. Sin texto ni imagen todavía. Corre solo los domingos 20:00.',
  },
  {
    n: 2, emoji: '✋', quien: 'vos', corto: 'Aprobás el plan',
    detalle: 'Aprobás los TEMAS de la semana, no las piezas. Todavía no hay texto ni imagen: eso viene en el paso 3.',
  },
  {
    n: 3, emoji: '✍️', quien: 'agente', corto: 'Gerente escribe',
    detalle: 'Recién acá aparecen el texto, los hashtags y el diseño de cada pieza. Corre todos los días y escribe de a 2 por corrida.',
  },
  {
    n: 4, emoji: '✅', quien: 'vos', corto: 'Aprobás cada pieza',
    detalle: 'Acá SÍ ves texto e imagen, como queda en el celular. Aprobar una pieza = queda programada.',
  },
  {
    n: 5, emoji: '🚀', quien: 'agente', corto: 'Sale a Instagram',
    detalle: 'El Publisher chequea cada 15 minutos y publica cada pieza sola cuando llega su horario. Nada sale sin tu OK del paso 4.',
  },
]

// Stepper visual de los 5 pasos. pasoActual = 1..5 (se resalta), los anteriores
// quedan marcados como hechos. Mobile-first: entra en 414px sin scroll.
export function FlujoStepper({ pasoActual }) {
  return (
    <div className="fr-stepper" role="list" aria-label="Los 5 pasos del flujo de Instagram">
      {PASOS_FLUJO.map((p, i) => {
        const estado = p.n < pasoActual ? 'hecho' : p.n === pasoActual ? 'actual' : 'futuro'
        return (
          <div key={p.n} className={`fr-paso fr-paso-${estado} fr-paso-${p.quien}`} role="listitem" title={p.detalle}>
            <div className="fr-paso-bola">{estado === 'hecho' ? '✓' : p.emoji}</div>
            <div className="fr-paso-num">{p.n}</div>
            <div className="fr-paso-txt">{p.corto}</div>
            <div className="fr-paso-quien">{p.quien === 'vos' ? 'VOS' : 'solo'}</div>
            {i < PASOS_FLUJO.length - 1 && <div className="fr-paso-linea" aria-hidden />}
          </div>
        )
      })}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// correrEdge: invoca una edge function con el token de la sesión del admin.
// ─────────────────────────────────────────────────────────────────────────────
export async function correrEdge(fn, body) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new Error('Sin sesión activa — recargá la página y entrá de nuevo')
  const r = await fetch(`${SUPA_URL}/functions/v1/${fn}`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  })
  const json = await r.json().catch(() => ({}))
  if (!r.ok || json.error) throw new Error(json.error || `HTTP ${r.status}`)
  return json
}

// Config de cada agente disparable a mano. okTexto(json) arma el feedback en criollo.
export const AGENTES_EDGE = {
  estratega: {
    fn: 'estratega-semanal',
    label: '🧠 Pedir plan ahora',
    working: 'El Estratega está pensando…',
    confirm: '¿Pedirle al Estratega el plan de la semana AHORA?\n\nLee el playbook, el banco de ángulos, las métricas y tus rechazos anteriores. Propone SOLO temas y horarios (el texto viene después). Toma 1-2 minutos.',
    body: { source: 'ui_manual' },
    okTexto: j => j.skip ? `ℹ️ ${j.resumen}` : `✓ Plan propuesto: ${j.piezas_propuestas} temas (${j.modelo || 'IA'}). Revisalo y aprobalo.`,
  },
  gerente: {
    fn: 'gerente-marketing',
    label: '✍️ Escribir ahora',
    working: 'El Gerente está escribiendo…',
    confirm: '¿Poner al Gerente a escribir AHORA?\n\nToma las piezas pendientes del plan aprobado y les escribe texto, hashtags y diseño. Escribe de a 2 por corrida (si faltan más, apretá de nuevo). Toma 1-3 minutos.',
    body: { auto: true, source: 'ui_manual' },
    okTexto: j => {
      const n = j.piezas_creadas?.length ?? 0
      return n > 0
        ? `✓ El Gerente escribió ${n} pieza${n !== 1 ? 's' : ''}. ${j.siguiente_paso || 'Revisalas en Piezas.'}`
        : `ℹ️ ${j.resumen || 'No había piezas pendientes para escribir.'}`
    },
  },
  publisher: {
    fn: 'publisher-marketing',
    label: '🚀 Publicar lo vencido ahora',
    working: 'El Publisher está publicando…',
    confirm: '¿Correr el Publisher AHORA?\n\nPublica en Instagram las piezas aprobadas cuyo horario ya pasó. Las que todavía no llegaron a su horario NO salen.',
    body: { source: 'ui_manual' },
    okTexto: j => `✓ Publisher: ${j.evaluadas ?? 0} evaluadas · ${j.publicadas ?? 0} publicadas${j.fallidas ? ` · ⚠ ${j.fallidas} fallidas` : ''}.`,
  },
  metricas: {
    fn: 'ig-metricas-pull',
    label: '📊 Traer métricas ahora',
    working: 'Trayendo métricas de IG…',
    confirm: '¿Traer las métricas de Instagram AHORA? (normalmente corre solo a las 22:00)',
    body: { source: 'ui_manual' },
    okTexto: () => '✓ Métricas de Instagram actualizadas.',
  },
  webscan: {
    fn: 'web-scan-comunicacion',
    label: '🔍 Escanear web ahora',
    working: 'Escaneando la web…',
    confirm: '¿Escanear toda la web ahora?\n\nEl analista recorre limpack.uy + tienda.limpack.uy y actualiza el banco de ángulos. Toma 1-2 minutos.',
    body: { source: 'ui_manual' },
    okTexto: j => `✓ Web escaneada: ${j.insertados ?? 0} ángulos nuevos, ${j.actualizados ?? 0} actualizados.`,
  },
}

// Botón "correr agente ahora": confirma, invoca, avisa qué pasó vía onMsg y
// refresca la página que lo contiene vía onDone.
export function BotonAgente({ agente, onMsg, onDone, className = 'com-btn com-btn-primary', label, body }) {
  const cfg = AGENTES_EDGE[agente]
  const [busy, setBusy] = useState(false)
  if (!cfg) return null
  async function go() {
    if (cfg.confirm && !window.confirm(cfg.confirm)) return
    setBusy(true)
    onMsg?.(null)
    try {
      const json = await correrEdge(cfg.fn, { ...cfg.body, ...(body || {}) })
      onMsg?.({ tipo: 'ok', texto: cfg.okTexto(json) })
      onDone?.(json)
    } catch (e) {
      onMsg?.({ tipo: 'err', texto: '⚠ ' + e.message })
    } finally {
      setBusy(false)
    }
  }
  return (
    <button type="button" onClick={go} disabled={busy} className={className}>
      {busy ? cfg.working : (label || cfg.label)}
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// cargarEstadoFlujo: UNA lectura de dónde está la semana. Devuelve:
//   plan   — último plan no descartado (o null)
//   filas  — filas de calendario_marketing de ese plan
//   c      — conteos (total, propuestas, esperanGerente, escritas, generadas,
//            programadas, vencidas, publicadas7d, fallidas)
//   paso   — 1..5, el paso "vivo" del stepper
//   titulo / detalle — la frase en criollo que resuelve el "no entiendo nada"
// ─────────────────────────────────────────────────────────────────────────────
export async function cargarEstadoFlujo() {
  const { data: plan } = await supabase
    .from('planes_semanales')
    .select('id, semana_inicio, resumen, estado, modelo, piezas_propuestas, created_at, aprobado_at')
    .neq('estado', 'descartado')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  let filas = []
  if (plan) {
    const { data: f } = await supabase
      .from('calendario_marketing')
      .select('id, fecha, hora_sugerida, canal, titulo, copy, estado')
      .eq('plan_id', plan.id)
      .order('fecha').order('hora_sugerida')
    filas = f || []
  }

  const hace7 = new Date(Date.now() - 7 * 86400000).toISOString()
  const ahora = new Date().toISOString()
  const [genR, progR, vencR, pub7R, fallR] = await Promise.all([
    supabase.from('posts_ig').select('id', { count: 'exact', head: true }).eq('estado', 'generado'),
    supabase.from('posts_ig').select('id', { count: 'exact', head: true }).in('estado', ['aprobado', 'programado']),
    supabase.from('posts_ig').select('id', { count: 'exact', head: true }).in('estado', ['aprobado', 'programado']).lte('horario_sugerido', ahora),
    supabase.from('posts_ig').select('id', { count: 'exact', head: true }).eq('estado', 'publicado').gte('publicado_at', hace7),
    supabase.from('posts_ig').select('id', { count: 'exact', head: true }).eq('estado', 'fallido'),
  ])

  const c = {
    total: filas.length,
    propuestas: filas.filter(x => x.estado === 'propuesto').length,
    esperanGerente: filas.filter(x => x.estado === 'pendiente').length,
    escritas: filas.filter(x => x.estado === 'en_proceso' || x.estado === 'publicado').length,
    generadas: genR.count || 0,
    programadas: progR.count || 0,
    vencidas: vencR.count || 0,
    publicadas7d: pub7R.count || 0,
    fallidas: fallR.count || 0,
  }

  // ── La lectura en criollo ──
  let paso, titulo, detalle
  if (!plan) {
    paso = 1
    titulo = 'Todavía no hay plan para esta semana'
    detalle = 'El Estratega corre solo los domingos a las 20:00 y deja acá su propuesta (temas y horarios, sin texto todavía). Si no querés esperar, pedíselo ahora con el botón.'
  } else if (plan.estado === 'propuesto') {
    paso = 2
    titulo = `Te toca a vos: hay un plan propuesto (semana del ${plan.semana_inicio})`
    detalle = `El Estratega propuso ${c.total} temas con fecha y horario. OJO: al aprobar todavía NO hay texto ni imagen — eso lo escribe el Gerente después, y cada pieza terminada la revisás una por una antes de que salga.`
  } else if (c.generadas > 0) {
    paso = 4
    const faltan = c.esperanGerente
    titulo = `Te toca a vos: ${c.generadas} pieza${c.generadas !== 1 ? 's' : ''} terminada${c.generadas !== 1 ? 's' : ''} esperan tu OK`
    detalle = `Plan aprobado ✓ · ${c.escritas} de ${c.total} piezas escritas.` +
      (faltan > 0 ? ` Faltan ${faltan}: el Gerente las escribe en su próxima corrida (todos los días, de a 2) o apuralo con "Escribir ahora".` : ' Revisalas: ahora sí ves texto e imagen de verdad.')
  } else if (c.esperanGerente > 0) {
    paso = 3
    titulo = `Plan aprobado ✓ · ${c.escritas} de ${c.total} piezas escritas`
    detalle = `Faltan ${c.esperanGerente}: el Gerente las escribe solo en su próxima corrida (corre todos los días, de a 2 por vez). No tenés nada que hacer… salvo que quieras apurarlo con el botón.`
  } else if (c.vencidas > 0) {
    paso = 5
    titulo = `${c.vencidas} pieza${c.vencidas !== 1 ? 's' : ''} aprobada${c.vencidas !== 1 ? 's' : ''} con horario vencido`
    detalle = 'El Publisher las publica en su próxima pasada (chequea cada 15 min). Si querés que salgan YA, apretá el botón.'
  } else if (c.programadas > 0) {
    paso = 5
    titulo = `Todo aprobado ✓ · ${c.programadas} programada${c.programadas !== 1 ? 's' : ''} en cola de salida`
    detalle = 'Salen solas cuando llega el horario de cada una (el Publisher chequea cada 15 minutos). No tenés nada pendiente.'
  } else {
    paso = 1
    titulo = 'Semana al día ✓ · nada te espera'
    detalle = (c.publicadas7d > 0 ? `Se publicaron ${c.publicadas7d} piezas en los últimos 7 días. ` : '') +
      'El próximo plan lo propone el Estratega el domingo 20:00 (o pedíselo antes con el botón).'
  }

  return { plan, filas, c, paso, titulo, detalle }
}

// ─────────────────────────────────────────────────────────────────────────────
// CasoActualCard — "dónde estamos parados". Recibe el resultado de
// cargarEstadoFlujo + onMsg/onDone para los botones de agentes.
// ─────────────────────────────────────────────────────────────────────────────
// paginaActual: 'panel' | 'plan' | 'piezas' — evita mostrar un link a la página
// en la que ya estás parado.
export function CasoActualCard({ est, onMsg, onDone, paginaActual = 'panel' }) {
  if (!est) return null
  const { plan, c, paso, titulo, detalle } = est
  const pasoDef = PASOS_FLUJO[paso - 1]
  const esTuTurno = pasoDef?.quien === 'vos'
  const enPlan = paginaActual === 'plan'
  const enPiezas = paginaActual === 'piezas'

  // Progreso de piezas escritas (solo con plan aprobado)
  const mostrarProgreso = plan?.estado === 'aprobado' && c.total > 0
  const pctEscritas = mostrarProgreso ? Math.round((c.escritas / c.total) * 100) : 0

  return (
    <section className={`com-card fr-caso ${esTuTurno ? 'fr-caso-vos' : ''}`}>
      <div className="fr-caso-head">
        <span className={`com-pill ${esTuTurno ? 'com-pill-gold' : 'com-pill-teal'}`}>
          PASO {paso} DE 5 · {esTuTurno ? '✋ TE TOCA A VOS' : '🤖 TRABAJAN LOS AGENTES'}
        </span>
      </div>
      <div className="fr-caso-titulo">{titulo}</div>
      <p className="fr-caso-detalle">{detalle}</p>

      {mostrarProgreso && (
        <div className="fr-caso-progreso">
          <div className="fr-caso-progreso-bar">
            <div className="fr-caso-progreso-fill" style={{ width: Math.max(3, pctEscritas) + '%' }} />
          </div>
          <div className="fr-caso-progreso-txt">
            {c.escritas} de {c.total} piezas escritas
            {c.generadas > 0 && ` · ${c.generadas} esperan tu OK`}
            {c.programadas > 0 && ` · ${c.programadas} programadas`}
            {c.fallidas > 0 && ` · ⚠ ${c.fallidas} fallidas`}
          </div>
        </div>
      )}

      <div className="fr-caso-acciones">
        {paso === 1 && !plan && (
          <BotonAgente agente="estratega" onMsg={onMsg} onDone={onDone} />
        )}
        {paso === 2 && !enPlan && (
          <Link to="/admin/comunicacion/plan" className="com-btn com-btn-green" style={{ textDecoration: 'none' }}>
            ✋ Revisar y aprobar el plan →
          </Link>
        )}
        {paso === 2 && enPlan && (
          <span className="com-card-sub">👇 El plan está acá abajo — revisalo y aprobalo.</span>
        )}
        {paso === 3 && (
          <>
            <BotonAgente agente="gerente" onMsg={onMsg} onDone={onDone} />
            {!enPiezas && (
              <Link to="/admin/comunicacion/piezas" className="com-btn com-btn-ghost" style={{ textDecoration: 'none' }}>
                Ver la cola de piezas →
              </Link>
            )}
          </>
        )}
        {paso === 4 && (
          <>
            {!enPiezas && (
              <Link to="/admin/comunicacion/piezas" className="com-btn com-btn-green" style={{ textDecoration: 'none' }}>
                ✅ Revisar y aprobar piezas →
              </Link>
            )}
            {enPiezas && <span className="com-card-sub">👇 Las piezas están acá abajo — tocá una y la ves como en Instagram.</span>}
            {c.esperanGerente > 0 && <BotonAgente agente="gerente" onMsg={onMsg} onDone={onDone} className="com-btn com-btn-ghost" />}
          </>
        )}
        {paso === 5 && (
          <>
            {c.vencidas > 0 && <BotonAgente agente="publisher" onMsg={onMsg} onDone={onDone} />}
            {!enPiezas && (
              <Link to="/admin/comunicacion/piezas" className="com-btn com-btn-ghost" style={{ textDecoration: 'none' }}>
                Ver la cola de salida →
              </Link>
            )}
          </>
        )}
        {paso === 1 && plan && (
          <BotonAgente agente="estratega" onMsg={onMsg} onDone={onDone} className="com-btn com-btn-ghost" label="🧠 Pedir el próximo plan ahora" />
        )}
      </div>
    </section>
  )
}

// CSS del stepper + tarjeta de caso. Se inyecta junto a COM_CSS en cada página.
export const FLUJO_CSS = `
  .fr-stepper {
    display: grid; grid-template-columns: repeat(5, 1fr); gap: 0;
    background: #fff; border: 1px solid #E5E7EB; border-radius: 14px;
    padding: 14px 8px 12px; box-shadow: 0 1px 3px rgba(26,52,97,.05);
  }
  .fr-paso { position: relative; display: flex; flex-direction: column; align-items: center; gap: 2px; text-align: center; padding: 0 4px; cursor: help; }
  .fr-paso-bola {
    width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center;
    justify-content: center; font-size: 15px; background: #F1F5F9; border: 2px solid #E2E8F0;
    position: relative; z-index: 1;
  }
  .fr-paso-linea {
    position: absolute; top: 17px; left: calc(50% + 19px); right: calc(-50% + 19px);
    height: 2px; background: #E2E8F0; z-index: 0;
  }
  .fr-paso-hecho .fr-paso-bola { background: #E0F4F6; border-color: #00A8B5; color: #00666B; font-weight: 900; }
  .fr-paso-hecho .fr-paso-linea { background: #00A8B5; }
  .fr-paso-actual .fr-paso-bola { box-shadow: 0 0 0 4px rgba(0,168,181,.18); border-color: #00A8B5; background: #fff; }
  .fr-paso-actual.fr-paso-vos .fr-paso-bola { box-shadow: 0 0 0 4px rgba(197,160,89,.25); border-color: #C5A059; }
  .fr-paso-num { font-size: 9px; font-weight: 800; color: #94A3B8; }
  .fr-paso-txt { font-size: 10px; font-weight: 700; color: #64748B; line-height: 1.15; }
  .fr-paso-actual .fr-paso-txt { color: #1A3461; }
  .fr-paso-quien { font-size: 8.5px; font-weight: 800; letter-spacing: .08em; color: #94A3B8; }
  .fr-paso-vos .fr-paso-quien { color: #C5A059; }
  .fr-paso-futuro { opacity: .6; }

  .fr-caso { border-left: 4px solid #00A8B5; }
  .fr-caso-vos { border-left-color: #C5A059; background: #FFFDF7; }
  .fr-caso-head { margin-bottom: 8px; }
  .fr-caso-titulo { font-size: 16.5px; font-weight: 900; color: #1A3461; letter-spacing: -.01em; }
  .fr-caso-detalle { font-size: 12.5px; color: #4B5563; line-height: 1.55; margin: 6px 0 0; max-width: 720px; }
  .fr-caso-progreso { margin-top: 12px; }
  .fr-caso-progreso-bar { height: 8px; background: #EEF1F5; border-radius: 999px; overflow: hidden; }
  .fr-caso-progreso-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg, #00A8B5, #5EE8F0); transition: width .3s; }
  .fr-caso-progreso-txt { font-size: 11.5px; color: #6B7280; margin-top: 5px; font-weight: 600; }
  .fr-caso-acciones { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 14px; }

  .fr-aviso {
    background: #FBF3E2; border: 1px solid #E9D9AE; border-left: 4px solid #C5A059;
    border-radius: 10px; padding: 12px 14px; font-size: 12.5px; color: #5c4a1e; line-height: 1.55;
  }
  .fr-aviso strong { color: #4a3a12; }

  @media (max-width: 480px) {
    .fr-stepper { padding: 12px 4px 10px; }
    .fr-paso-bola { width: 28px; height: 28px; font-size: 13px; }
    .fr-paso-linea { top: 14px; left: calc(50% + 16px); right: calc(-50% + 16px); }
    .fr-paso-txt { font-size: 8.5px; }
  }
`
